import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert, Spinner, Badge, Row, Col } from 'react-bootstrap';
import apiCall from '../../shared/apiCall';
import { ApiActions } from '../../shared/apiActions';
import { formatDateWithLocale } from '../../shared/utils';

// Verify UPDATE_SETTINGS is available
if (!ApiActions.UPDATE_SETTINGS) {
  console.error('UPDATE_SETTINGS not found in ApiActions. Available actions:', Object.keys(ApiActions));
}

export default function CurrencyManagement() {
  const [currencies, setCurrencies] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentCurrency, setCurrentCurrency] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    symbol: '',
    dollarRate: ''
  });

  // Load currencies on mount
  useEffect(() => {
    loadCurrencies();
  }, []);

  const loadCurrencies = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiCall(
        `/api/model?action=${ApiActions.GET_SETTINGS}&key=supportedCurrencies`,
        {},
        'GET'
      );

      if (response.success && response.data) {
        setCurrencies(response.data.currencies || []);
        setDefaultCurrency(response.data.defaultCurrency || 'USD');
        setLastRefreshTime(response.data.lastRefreshTime || null);
      } else {
        setError('Failed to load currencies');
      }
    } catch (err) {
      console.error('Error loading currencies:', err);
      setError('Error loading currencies: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveCurrencies = async (updatedCurrencies, updatedDefaultCurrency) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const settingsData = {
        currencies: updatedCurrencies,
        defaultCurrency: updatedDefaultCurrency || defaultCurrency
      };

      // Ensure UPDATE_SETTINGS is defined (fallback to 502 if undefined due to cache issues)
      const updateAction = ApiActions.UPDATE_SETTINGS || 502;
      
      if (!updateAction || updateAction === 'undefined') {
        console.error('UPDATE_SETTINGS is not defined in ApiActions:', ApiActions);
        console.error('Available actions:', Object.keys(ApiActions));
        setError('Configuration error: UPDATE_SETTINGS action not found. Please refresh the page.');
        setSaving(false);
        return;
      }
      
      console.log('Calling UPDATE_SETTINGS with action:', updateAction);
      
      const response = await apiCall(
        `/api/model?action=${updateAction}`,
        {
          key: 'supportedCurrencies',
          value: settingsData,
          type: 'json'
        },
        'POST'
      );

      if (response.success) {
        setCurrencies(updatedCurrencies);
        setDefaultCurrency(updatedDefaultCurrency || defaultCurrency);
        setSuccess('Currencies saved successfully!');
        
        // Clear localStorage cache to force refresh
        localStorage.removeItem('tripsy_currencies');
        localStorage.removeItem('tripsy_currencies_timestamp');
        
        // Close modals
        setShowAddModal(false);
        setShowEditModal(false);
        setShowDeleteModal(false);
        resetForm();
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to save currencies');
      }
    } catch (err) {
      console.error('Error saving currencies:', err);
      setError('Error saving currencies: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEdit = (currency) => {
    setCurrentCurrency(currency);
    setFormData({
      code: currency.code,
      name: currency.name,
      symbol: currency.symbol,
      dollarRate: currency.dollarRate.toString()
    });
    setShowEditModal(true);
  };

  const handleDelete = (currency) => {
    setCurrentCurrency(currency);
    setShowDeleteModal(true);
  };

  const handleSaveAdd = () => {
    // Validate form
    if (!formData.code || !formData.name || !formData.symbol || !formData.dollarRate) {
      setError('All fields are required');
      return;
    }

    // Check if code already exists
    if (currencies.some(c => c.code.toUpperCase() === formData.code.toUpperCase())) {
      setError('Currency code already exists');
      return;
    }

    const newCurrency = {
      code: formData.code.toUpperCase(),
      name: formData.name,
      symbol: formData.symbol,
      dollarRate: parseFloat(formData.dollarRate)
    };

    if (isNaN(newCurrency.dollarRate) || newCurrency.dollarRate <= 0) {
      setError('Dollar rate must be a positive number');
      return;
    }

    const updatedCurrencies = [...currencies, newCurrency];
    saveCurrencies(updatedCurrencies);
  };

  const handleSaveEdit = () => {
    // Validate form
    if (!formData.code || !formData.name || !formData.symbol || !formData.dollarRate) {
      setError('All fields are required');
      return;
    }

    const dollarRate = parseFloat(formData.dollarRate);
    if (isNaN(dollarRate) || dollarRate <= 0) {
      setError('Dollar rate must be a positive number');
      return;
    }

    const updatedCurrencies = currencies.map(c =>
      c.code === currentCurrency.code
        ? {
            code: formData.code.toUpperCase(),
            name: formData.name,
            symbol: formData.symbol,
            dollarRate: dollarRate
          }
        : c
    );

    // If default currency code changed, update it
    let updatedDefault = defaultCurrency;
    if (currentCurrency.code === defaultCurrency && formData.code.toUpperCase() !== defaultCurrency) {
      updatedDefault = formData.code.toUpperCase();
    }

    saveCurrencies(updatedCurrencies, updatedDefault);
  };

  const handleConfirmDelete = () => {
    if (!currentCurrency) return;

    // Don't allow deleting the default currency
    if (currentCurrency.code === defaultCurrency) {
      setError('Cannot delete the default currency. Please set another currency as default first.');
      setShowDeleteModal(false);
      return;
    }

    const updatedCurrencies = currencies.filter(c => c.code !== currentCurrency.code);
    saveCurrencies(updatedCurrencies);
  };

  const handleSetDefault = (code) => {
    if (code === defaultCurrency) return;
    saveCurrencies(currencies, code);
  };

  const handleRefreshRates = async () => {
    try {
      setRefreshing(true);
      setError('');
      setSuccess('');

      const refreshAction = ApiActions.REFRESH_CURRENCY_RATES || 503;
      
      if (!refreshAction) {
        setError('Configuration error: REFRESH_CURRENCY_RATES action not found');
        setRefreshing(false);
        return;
      }

      const response = await apiCall(
        `/api/model?action=${refreshAction}`,
        {},
        'POST'
      );

      if (response.success) {
        // Update currencies with new rates
        if (response.currencies && response.currencies.length > 0) {
          setCurrencies(response.currencies);
        }
        
        // Update last refresh time
        if (response.lastRefreshTime) {
          setLastRefreshTime(response.lastRefreshTime);
        } else {
          setLastRefreshTime(new Date().toISOString());
        }
        
        // Clear localStorage cache to force refresh
        localStorage.removeItem('tripsy_currencies');
        localStorage.removeItem('tripsy_currencies_timestamp');
        
        setSuccess(response.message || `Successfully refreshed rates. Updated ${response.updatedCount || 0} currencies.`);
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(response.message || 'Failed to refresh currency rates');
      }
    } catch (err) {
      console.error('Error refreshing rates:', err);
      setError('Error refreshing rates: ' + err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      symbol: '',
      dollarRate: ''
    });
    setCurrentCurrency(null);
    setError('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading currencies...</p>
        </Card.Body>
      </Card>
    );
  }

  const formatLastRefreshTime = (timeString) => {
    if (!timeString) return null;
    try {
      const date = new Date(timeString);
      return formatDateWithLocale(date, 'PPpp');
    } catch (e) {
      return timeString;
    }
  };

  const getOneDollarWorth = (dollarRate) => {
    if (!dollarRate || dollarRate === 0) return 'N/A';
    return (1 / dollarRate).toFixed(4);
  };

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
          <div className="mb-2 mb-md-0">
            <h5 className="mb-1">
              <i className="bi bi-currency-exchange me-2"></i>
              Supported Currencies
            </h5>
            {lastRefreshTime && (
              <small className="text-muted">
                <i className="bi bi-clock-history me-1"></i>
                Last refreshed: {formatLastRefreshTime(lastRefreshTime)}
              </small>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button 
              variant="primary" 
              size="sm" 
              onClick={handleRefreshRates}
              disabled={refreshing || saving || currencies.length === 0}
            >
              {refreshing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh Rates
                </>
              )}
            </Button>
            <Button variant="success" size="sm" onClick={handleAdd}>
              <i className="bi bi-plus-circle me-1"></i>
              Add Currency
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {currencies.length === 0 ? (
            <Alert variant="info">
              No currencies found. Click "Add Currency" to add your first currency.
            </Alert>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Symbol</th>
                      <th>Dollar Rate</th>
                      <th>1$ Worth</th>
                      <th>Default</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currencies.map((currency) => (
                      <tr key={currency.code}>
                        <td><strong>{currency.code}</strong></td>
                        <td>{currency.name}</td>
                        <td>{currency.symbol}</td>
                        <td>{currency.dollarRate.toFixed(4)}</td>
                        <td>{getOneDollarWorth(currency.dollarRate)}</td>
                        <td>
                          {currency.code === defaultCurrency ? (
                            <Badge bg="success">Default</Badge>
                          ) : (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleSetDefault(currency.code)}
                              disabled={saving}
                            >
                              Set Default
                            </Button>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleEdit(currency)}
                            disabled={saving}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(currency)}
                            disabled={saving || currency.code === defaultCurrency}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="d-md-none">
                <Row>
                  {currencies.map((currency) => (
                    <Col xs={12} key={currency.code} className="mb-3">
                      <Card>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <h6 className="mb-0">
                                <strong>{currency.code}</strong> - {currency.name}
                              </h6>
                              <small className="text-muted">{currency.symbol}</small>
                            </div>
                            {currency.code === defaultCurrency && (
                              <Badge bg="success">Default</Badge>
                            )}
                          </div>
                          <hr />
                          <div className="mb-2">
                            <strong>Dollar Rate:</strong> {currency.dollarRate.toFixed(4)}
                          </div>
                          <div className="mb-3">
                            <strong>1$ Worth:</strong> {getOneDollarWorth(currency.dollarRate)}
                          </div>
                          <div className="d-flex gap-2">
                            {currency.code !== defaultCurrency && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleSetDefault(currency.code)}
                                disabled={saving}
                                className="flex-fill"
                              >
                                Set Default
                              </Button>
                            )}
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEdit(currency)}
                              disabled={saving}
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(currency)}
                              disabled={saving || currency.code === defaultCurrency}
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Add Currency Modal */}
      <Modal show={showAddModal} onHide={() => { setShowAddModal(false); resetForm(); }}>
        <Modal.Header closeButton>
          <Modal.Title>Add Currency</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Currency Code *</Form.Label>
              <Form.Control
                type="text"
                name="code"
                value={formData.code}
                onChange={handleFormChange}
                placeholder="e.g., USD, EUR, GBP"
                maxLength={3}
                style={{ textTransform: 'uppercase' }}
              />
              <Form.Text className="text-muted">3-letter ISO currency code</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Currency Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                placeholder="e.g., US Dollar, Euro"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Symbol *</Form.Label>
              <Form.Control
                type="text"
                name="symbol"
                value={formData.symbol}
                onChange={handleFormChange}
                placeholder="e.g., $, €, £"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Dollar Rate *</Form.Label>
              <Form.Control
                type="number"
                name="dollarRate"
                value={formData.dollarRate}
                onChange={handleFormChange}
                placeholder="e.g., 1.0"
                step="0.0001"
                min="0.0001"
              />
              <Form.Text className="text-muted">
                How many USD one unit of this currency is worth (e.g., 1 EUR = 1.10 USD means dollarRate = 1.10)
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowAddModal(false); resetForm(); }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveAdd} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Add Currency'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Currency Modal */}
      <Modal show={showEditModal} onHide={() => { setShowEditModal(false); resetForm(); }}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Currency</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Currency Code *</Form.Label>
              <Form.Control
                type="text"
                name="code"
                value={formData.code}
                onChange={handleFormChange}
                placeholder="e.g., USD, EUR, GBP"
                maxLength={3}
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Currency Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                placeholder="e.g., US Dollar, Euro"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Symbol *</Form.Label>
              <Form.Control
                type="text"
                name="symbol"
                value={formData.symbol}
                onChange={handleFormChange}
                placeholder="e.g., $, €, £"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Dollar Rate *</Form.Label>
              <Form.Control
                type="number"
                name="dollarRate"
                value={formData.dollarRate}
                onChange={handleFormChange}
                placeholder="e.g., 1.0"
                step="0.0001"
                min="0.0001"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowEditModal(false); resetForm(); }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => { setShowDeleteModal(false); resetForm(); }}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Currency</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete <strong>{currentCurrency?.code}</strong> ({currentCurrency?.name})?</p>
          <Alert variant="warning">
            <strong>Warning:</strong> This action cannot be undone. Make sure this currency is not being used in any plans.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDeleteModal(false); resetForm(); }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete Currency'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

